import { getCompletedYears } from '../lib/experience';
import employmentHistory from './employmentHistory.json';

export const personalInfo = {
  name: "Karim Chaouki",
  headline: "Your Strategic Partner for HCM & ERP Transformation",
  subtitle: "Senior Project Implementation Consultant | PMP | RMP | Scrum Master | Bilingual EN/FR",
  email: "karim.chaouki@gmail.com",
  phone: "437-688-9403",
  linkedin: "https://www.linkedin.com/in/karimchaouki",
  location: "Toronto, ON, Canada",
  resumeUrl: "/resume/KC_PM_Resume.pdf",
  languages: [
    { lang: "English", level: "Fluent" },
    { lang: "French", level: "Fluent" },
    { lang: "Arabic", level: "Native" },
  ],
  summary:
    "Strategic Project and Program Manager with 7+ years of progressive experience in HCM software implementation, multi-project portfolio management, and cross-border client onboarding. Currently leading implementations in ADP's Center of Excellence for enterprise clients across Canada and the United States.",
};

const getClientsServed = () => {
  const baseValue = 100;
  const startDate = new Date('2026-09-25T00:00:00-04:00');
  const now = new Date();

  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth();
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth();

  const monthsElapsed = (nowYear - startYear) * 12 + (nowMonth - startMonth);
  return Math.max(baseValue, baseValue + monthsElapsed);
};

export const stats = [
  { value: getClientsServed, suffix: "+", label: "Clients Served" },
  // Cumulative professional experience, computed from verified employment
  // dates in employmentHistory.json (updates automatically over time).
  { value: getCompletedYears(employmentHistory), suffix: "+", label: "Years Experience" },
  { value: 100, suffix: "%", label: "On-Time Delivery" },
  { value: 95, suffix: "%", label: "Client Retention" },
];

export const differentiators = [
  {
    title: "Multi-Jurisdiction Payroll Expert",
    description:
      "Deep expertise in US/Canada cross-border payroll, tax compliance, and multi-province/multi-state HCM deployments.",
    icon: "globe",
  },
  {
    title: "Certified Risk Manager",
    description:
      "RMP-certified professional adept at identifying, mitigating, and managing complex project risks across enterprise implementations.",
    icon: "shield",
  },
  {
    title: "Enterprise HCM Specialist",
    description:
      "ADP Center of Excellence lead with hands-on mastery of Workforce Now suite — payroll, benefits, time, talent, and HR workflows.",
    icon: "building",
  },
  {
    title: "Change Management Leader",
    description:
      "Proven ability to drive organizational transformation through structured methodologies, stakeholder engagement, and comprehensive training programs.",
    icon: "trendingUp",
  },
];

export const expertise = [
  {
    name: "HCM & HRIS",
    icon: "users",
    items: ["ADP Workforce Now", "System Configuration", "Client Onboarding"],
  },
  {
    name: "Payroll Systems",
    icon: "calculator",
    items: ["US/Canada Payroll", "Multi-Jurisdiction", "Tax Compliance"],
  },
  {
    name: "Agile & Scrum",
    icon: "zap",
    items: ["Sprint Planning", "Daily Standups", "Retrospectives"],
  },
  {
    name: "Risk Management",
    icon: "shieldAlert",
    items: ["RAID Logs", "Mitigation Strategies", "Contingency Planning"],
  },
  {
    name: "Stakeholder Engagement",
    icon: "messageSquare",
    items: ["Executive Communication", "Requirements Gathering", "Gap Analysis"],
  },
  {
    name: "Cross-Border Compliance",
    icon: "landmark",
    items: ["US/Canada Regulations", "AML/KYC", "PCMLTF"],
  },
  {
    name: "Data Migration",
    icon: "database",
    items: ["ETL Processes", "Data Validation", "System Integration"],
  },
  {
    name: "Training & Mentorship",
    icon: "graduationCap",
    items: ["Program Design", "Knowledge Transfer", "Team Development"],
  },
];

export const experience = [
  {
    company: "ADP Canada",
    subtitle: "Center of Excellence",
    role: "Senior Project Implementation Consultant",
    period: "2023 – Present",
    location: "Toronto, ON",
    achievements: [
      "Lead HCM implementation projects specializing in complex cross-border deployments for enterprise clients across Canada and the US",
      "Managed end-to-end implementation of ADP Workforce Now HCM suite for 50+ clients with 100% on-time completion rate",
      "Achieved 20% improvement in client satisfaction scores through structured implementation methodology",
      "Reduced setup errors by 30% and improved system accuracy to 98%",
      "Mentored 15+ new implementation consultants, accelerating ramp-up time by 25%",
    ],
  },
  {
    company: "Laurentian Bank",
    subtitle: "Financial Intelligence Unit",
    role: "Analyst, Financial Intelligence Unit",
    period: "2022 – 2023",
    location: "Toronto, ON",
    achievements: [
      "Led implementation of new analytical frameworks for AML/KYC compliance, increasing detection by 30%",
      "Prepared detailed compliance reports with 100% accuracy supporting FINTRAC audits",
      "Reduced response times to suspicious activity alerts by 25%",
    ],
  },
  {
    company: "Laurentian Bank",
    subtitle: "Customer Service",
    role: "Bilingual Senior Customer Service Officer",
    period: "2018 – 2022",
    location: "Toronto, ON",
    achievements: [
      "Contributed to 15% increase in customer retention rates maintaining 95% client satisfaction",
      "Reduced average wait times by 30% through cross-functional optimization projects",
      "Delivered training programs for 12+ officers improving onboarding efficiency by 30%",
    ],
  },
  {
    company: "Akalee",
    subtitle: "Project Management",
    role: "Project Coordinator",
    period: "2017 – 2018",
    location: "Casablanca, Morocco",
    achievements: [
      "Achieved 100% project deadline adherence rate across multiple concurrent initiatives",
      "Increased new client accounts by 30% and maintained 95% retention rate",
      "Reduced project delays by 20% through comprehensive risk analyses",
    ],
  },
  {
    company: "Société Générale",
    subtitle: "Banking Operations",
    role: "Customer Relationship Manager",
    period: "2001 – 2015",
    location: "Casablanca, Morocco",
    achievements: [
      "Recognized as Employee of the Year (2007) achieving 300% of acquisition targets",
      "Managed high-value client portfolio exceeding 500 accounts with 40% retention increase",
      "Contributed to 35% increase in cross-selling of banking products and services",
    ],
  },
];

export const certifications = [
  {
    name: "PMP",
    fullName: "Project Management Professional",
    org: "Project Management Institute",
    year: "2020",
    icon: "award",
  },
  {
    name: "RMP",
    fullName: "Risk Management Professional",
    org: "Project Management Institute",
    year: "2023",
    icon: "shieldCheck",
  },
  {
    name: "CSM",
    fullName: "Certified Scrum Master",
    org: "Scrum Alliance",
    year: "2023",
    icon: "refreshCw",
  },
  {
    name: "FIS",
    fullName: "Financial Intelligence Specialist",
    org: "ManchesterCF Institute",
    year: "2023",
    icon: "bookOpen",
  },
];

export const metrics = [
  { value: "20%", label: "CSAT Improvement" },
  { value: "30%", label: "Fewer Setup Errors" },
  { value: "15%", label: "Ops Efficiency Boost" },
  { value: "25%", label: "Faster Issue Resolution" },
  { value: "15+", label: "Consultants Mentored" },
  { value: () => `${getClientsServed()}+`, label: "Enterprise Clients" },
  { value: "98%", label: "System Accuracy" },
  { value: "100%", label: "On-Time Delivery" },
];

export const education = [
  {
    degree: "MBA in Finance",
    school: "Cardiff Metropolitan University",
    location: "London, UK",
    period: "2015 – 2016",
  },
  {
    degree: "Master's in Management & Strategy",
    school: "Collège de Sherbrooke",
    location: "Casablanca, Morocco",
    period: "2009 – 2011",
  },
  {
    degree: "Bachelor's in Marketing",
    school: "Collège de Sherbrooke",
    location: "Casablanca, Morocco",
    period: "2007 – 2009",
  },
];

export const testimonials = [
  {
    name: "Nevine Beshara, PCP, BCom.",
    role: "Senior Implementation Specialist",
    company: "ADP",
    relationship: "ADP Team Member",
    text: "Karim brought a strong combination of client-centric focus, technical expertise, and a proactive mindset that made him a reliable go-to person for both our clients and internal teams. His deep understanding of implementation processes, combined with his fluency in both English and French, allowed him to seamlessly guide clients through every phase — ensuring successful launches and high client satisfaction. If you're looking for someone who blends strategic thinking with hands-on execution and always puts the client first, Karim is that person.",
  },
  {
    name: "Jessica Phillips",
    role: "Technical Consultant",
    company: "Dayforce",
    relationship: "Karim's Mentor",
    text: "Karim possessed an insatiable appetite for learning, coupled with an innate desire to assist his colleagues whenever needed. He consistently demonstrated an unwavering commitment to excellence, exemplified in his honorable work ethic and dedication to achieving nothing short of his best. Beyond his impressive certifications, Karim's most striking qualities lie in his compassion, patience, and kindness towards others. I am confident that his talent and dedication will continue to shine brightly wherever his career path may lead.",
  },
  {
    name: "Dr. Said El Mansour Cherkaoui",
    role: "Strategic Catalyst & Policy Adviser",
    company: "International Affairs",
    relationship: "Karim's Mentor",
    text: "Karim has crossed many countries in terms of business cultures and operational management. He has extensive work experience in banking within international institutions. He has also extended his practical knowledge of management by managing directly in Canada several services offering direct solutions to clients at the level of finance, insurance and banking. Karim has been always following and adapting new solutions to every change occurred in his domain of engagement.",
  },
  {
    name: "Tanvi Patel",
    role: "Financial Services Professional",
    company: "Wealthsimple (Formerly Laurentian Bank)",
    relationship: "Colleague",
    text: "It's rare that you come across a standout leader like Karim. I've had the opportunity to work closely with him for two years. He is a fountain of knowledge and experience and always makes time to teach patiently while completing all of his other responsibilities. I continue to be impressed by Karim's ability to handle the toughest clients and situations effortlessly while maintaining objectivity and a positive outlook. Karim earns my highest recommendation.",
  },
  {
    name: "Affie Mobin, PMP",
    role: "AVP, Unsecured Lending & Credit Cards",
    company: "Laurentian Bank",
    relationship: "Cross-Team Colleague",
    text: "I have worked with Karim over the last couple of years and have found him to be a breath of fresh air. He has a customer first approach and has championed multiple innovative ideas and solutions. I have seen instances where Karim has influenced leadership and pushed for cost savings and efficiency enhancements. Overall Karim is a great contributor and delivers value and is passionate about serving our customers.",
  },
  {
    name: "Christina Hutchinson, CTDP, M.Ed.",
    role: "Senior Learning Manager/Partner",
    company: "ERPM",
    relationship: "Colleague",
    text: "Karim is an outstanding team player who exhibits a great amount of passion in what he does. His openness to sharing his extensive knowledge in the banking industry proved to be invaluable to new hires. Karim communicates ideas effectively by relating with those he engages with and his passion and knowledge are unmatched. I would recommend Karim if you're looking for a superstar who inspires others with his ambition to succeed.",
  },
  {
    name: "ASAD DARHBAR",
    role: "Cyber Security Consultant",
    company: "CISSP, CRISC, CISM, CISA",
    relationship: "Karim's Client",
    text: "Karim has strong technical and management skills especially in project management. He has a large experience in managing projects even in complex environments. Karim is a hard worker who always respects his engagements, with a big capacity of listening, communicating and leading a group to realize objectives. I really recommend him as a project manager.",
  },
  {
    name: "Kiran Patel",
    role: "Manager, Administration",
    company: "Laurentian Bank",
    relationship: "Direct Manager",
    text: "I was impressed with Karim's ability to handle any situation calmly and patiently, even with the toughest and difficult callers. This skill of de-escalating and building positive customer experiences created loyalty. Karim is a valuable asset to any organization.",
  },
  {
    name: "Muhsin A.",
    role: "Sr. Implementation Specialist, HR",
    company: "ADP",
    relationship: "ADP Team Member",
    text: "I would say he is among one of the top in regards to his position at ADP. As a team, he has always been present to resolve any issues, very professional and is always on top of his things. It has always been a pleasure working alongside Karim.",
  },
  {
    name: "Laurent Ngono Kede, CIA",
    role: "Lead Risk Internal Auditor",
    company: "Laurentian Bank",
    relationship: "Colleague",
    text: "Supportive, knowledgeable, inspirational and highly committed are strengths that describe Karim. We had the opportunity to collaborate at Laurentian Bank, thus I can testify how trustworthy Karim is. Working with Karim has been my best professional experience since I started my career.",
  },
];

export const navLinks = [
  { label: "About", href: "#about" },
  { label: "Blog", href: "/blog", route: true },
  { label: "Expertise", href: "#expertise" },
  { label: "Experience", href: "#experience" },
  { label: "Certifications", href: "#certifications" },
  { label: "Impact", href: "#impact" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];
